import { useMemo } from 'react';
import useRegisterGlobalNudgesCallback, {
  NUDGE_TYPE_KEY
} from '~/modules/resource-management/resourceUserDetails/hooks/useRegisterGlobalNudgesCallback';
import useUserProfileCertificates from './useUserProfileCertificates';
import { combineCertificatesAndUserProfileCertificates } from './combineCertificates';
import { useOnAcceptCertificateNudges } from './useOnAcceptCertificateNudges';
import { useOnRejectCertificateNudges } from './useOnRejectCertificateNudges';

export const useCertificateNudgeActions = ({
  userUri,
  userSlug,
  certificates,
  editable,
  canCreateSkills,
  isPsaPraaUserProfileNudgesEnabled
}) => {
  const {
    userProfileCertificates,
    loading: isUserProfileCertificatesLoading
  } = useUserProfileCertificates({
    userUri,
    skip: !(isPsaPraaUserProfileNudgesEnabled && canCreateSkills)
  });

  const combinedCertificates = useMemo(
    () =>
      isPsaPraaUserProfileNudgesEnabled
        ? combineCertificatesAndUserProfileCertificates({
            certificates,
            userProfileCertificates
          })
        : [],
    [certificates, userProfileCertificates, isPsaPraaUserProfileNudgesEnabled]
  );

  const { onAcceptCertificateNudges } = useOnAcceptCertificateNudges({
    userUri,
    userSlug
  });
  const { onRejectCertificateNudges } = useOnRejectCertificateNudges({
    userUri
  });

  const filteredCertificateNudges = useMemo(
    () =>
      combinedCertificates.filter(
        certificate => certificate.isDelaRecommendation
      ),
    [combinedCertificates]
  );

  const acceptAllCertificateNudges = useMemo(
    () =>
      filteredCertificateNudges?.length
        ? () => onAcceptCertificateNudges(filteredCertificateNudges)
        : null,
    [onAcceptCertificateNudges, filteredCertificateNudges]
  );

  const rejectAllCertificateNudges = useMemo(
    () =>
      filteredCertificateNudges?.length
        ? () => onRejectCertificateNudges(filteredCertificateNudges)
        : null,
    [onRejectCertificateNudges, filteredCertificateNudges]
  );

  useRegisterGlobalNudgesCallback({
    nudgeTypeKey: NUDGE_TYPE_KEY.CERTIFICATE_NUDGES,
    editable: editable && canCreateSkills,
    onAccept:
      acceptAllCertificateNudges && canCreateSkills
        ? acceptAllCertificateNudges
        : null,
    onReject:
      rejectAllCertificateNudges && canCreateSkills
        ? rejectAllCertificateNudges
        : null
  });

  return {
    isUserProfileCertificatesLoading,
    combinedCertificates,
    onAcceptCertificateNudges,
    onRejectCertificateNudges,
    acceptAllCertificateNudges,
    rejectAllCertificateNudges
  };
};

export default useCertificateNudgeActions;
